/**
 * downloads.ts
 * Source unique des applications téléchargeables (mobile + desktop).
 * Utilisé à la fois par le composant de téléchargement de la page d'accueil
 * (MobileAppSection) et par le bouton discret des pages de détail (AppDetail).
 *
 * Les liens Google Drive sont en format `uc?export=download&id=...` pour
 * déclencher le téléchargement direct plutôt que d'ouvrir le viewer Drive.
 * Pour les APK Android (> 100 MB), Drive intercale un avertissement antivirus
 * que l'utilisateur valide avant le download (inévitable sans CDN dédié).
 *
 * WINDOWS : ZIP portable (dézipper puis lancer wanzo.exe, garder le dossier
 * entier). Un MSIX auto-signé échoue à l'install côté public (0x800B010A) ;
 * MSIX signé-AC / Store à venir.
 *
 * Builds du 5 septembre 2026.
 */

import businessLogo from '../assets/images/apps/business.png';
import productionLogo from '../assets/images/apps/production.png';
import assetsLogo from '../assets/images/apps/assets.png';
import avecLogo from '../assets/images/apps/avec.png';

export interface DownloadLinks {
  android?: string;
  windows?: string;
  /**
   * Variante Android pour terminaux de caisse a imprimante integree
   * (Qunsuo PDA3505 / 3506 et equivalents, Android 6) : leur pilote
   * d'impression n'existe qu'en 32 bits. Proposee discretement sous le
   * bouton Android ; la version standard reste celle de tous les autres.
   */
  androidPos32?: string;
}

export interface DownloadableApp {
  /** Identifiant, aligné sur appDetails.ts pour lier vers la page de détail. */
  id: string;
  /** Nom métier clair, pour ne pas se tromper d'application. */
  name: string;
  /** Public visé : aide l'utilisateur à choisir la bonne app. */
  audience: string;
  /** Brève description. */
  description: string;
  logo: string;
  downloads: DownloadLinks;
}

const drive = (fileId: string) =>
  `https://drive.google.com/uc?export=download&id=${fileId}`;

/** Lien de l'APK Wanzo AVEC (com.wanzo.avec), distribution Google Drive. */
export const AVEC_ANDROID_DOWNLOAD_URL = drive('1psBa_sscjvx-bNBcsdnn8PyzjGqWROI3');

export const downloadableApps: DownloadableApp[] = [
  {
    id: 'business',
    name: 'Point de vente et Atelier',
    audience: 'Boutiques, restaurants, ateliers, garages, salons',
    description:
      "Vendez, encaissez et suivez vos stocks et vos clients, en boutique comme à l'atelier.",
    logo: businessLogo,
    downloads: {
      android: drive('1ze6NUW0FPY626OtPMvs0CJUXJ3OtvYzo'),
      androidPos32: drive('1owPDhIoE2xqGCVa7aBIZ3IfZD8PQpbqo'),
      windows: drive('1eWxN3drxghuVxFkDt5cMt6VUb_9Gi7N8'),
    },
  },
  {
    id: 'production',
    name: 'Production',
    audience: 'Fabricants, transformateurs, imprimeries',
    description:
      "Pilotez votre atelier de production, de l'achat des matières au produit fini et son coût réel.",
    logo: productionLogo,
    downloads: {
      android: drive('1l0e70DLTk0E-APjeXMLWKaetZrdjmemM'),
      windows: drive('1L0a58KhnfPsxQUfGYdjZtBnyIcAwVxNx'),
    },
  },
  {
    id: 'assets',
    name: "Gestion d'actifs",
    audience: 'Hôtels, salles de fête, espaces de loisir, suivi des biens',
    description:
      "Gérez réservations, biens et charroi : hôtellerie, événementiel et suivi de vos actifs.",
    logo: assetsLogo,
    downloads: {
      android: drive('1mPSO6f_0p8EK3HKXeJMhrgqx-1itzC2c'),
      windows: drive('1qh1aC0zP91GDnz28AJ9cjpnenhJ-tQLY'),
    },
  },
  {
    id: 'avec',
    name: 'Wanzo AVEC',
    audience: "Dirigeants d'AVEC (président, secrétaire, trésorier) et agents terrain",
    description:
      "Tenez les réunions de votre AVEC sur téléphone, même sans réseau : parts, solidarité, crédits, AGR et carnets des membres.",
    logo: avecLogo,
    downloads: {
      android: AVEC_ANDROID_DOWNLOAD_URL,
    },
  },
];

/** Texte d'aide commun : a qui s'adresse la version POS 32 bits. */
export const POS32_HINT =
  "Terminal de caisse avec imprimante intégrée (PDA3505, Android 6) ? Prenez cette version, sinon gardez la version Android standard.";

export const getDownloadableApp = (id: string): DownloadableApp | undefined =>
  downloadableApps.find((a) => a.id === id);
